import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { StatusKind, StatusPillComponent } from '../../../components/status-pill/status-pill.component';
import { Release } from '../../../models/release.model';
import { ReleaseService } from '../../../services/release.service';
import { formatIsoDateDe } from '../../../shared/date.util';
import { ReleaseDialogComponent } from '../release-dialog/release-dialog.component';

/**
 * Karte „Releases“ im Projektformular (Aufbau wie „Repositories“ in page-projekt-neu.jsx:426-451,
 * Eintrag NEX-1972 18221). Lädt ihre Liste selbst; gespeichert wird nur über den Dialog, nie über
 * das Projekt.
 */
@Component({
  selector: 'app-release-karte',
  imports: [TranslatePipe, MatButtonModule, MatIcon, StatusPillComponent, ReleaseDialogComponent],
  templateUrl: './release-karte.component.html',
  styleUrl: './release-karte.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReleaseKarteComponent implements OnInit {
  private readonly service = inject(ReleaseService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();

  readonly releases = signal<Release[]>([]);
  readonly ladestatus = signal<'laedt' | 'geladen' | 'fehler'>('laedt');
  readonly vorhandeneIds = computed(() => this.releases().map((r) => r.id));
  readonly dialogOffen = signal(false);
  readonly ausgewaehlt = signal<Release | null>(null);

  ngOnInit(): void {
    this.laden();
  }

  laden(): void {
    this.ladestatus.set('laedt');
    this.service
      .list(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (liste) => {
          this.releases.set(liste);
          this.ladestatus.set('geladen');
        },
        error: () => this.ladestatus.set('fehler'),
      });
  }

  hinzufuegen(): void {
    this.oeffnen(null);
  }

  bearbeiten(release: Release): void {
    this.oeffnen(release);
  }

  stichtag(release: Release): string {
    return release.stichtag ? formatIsoDateDe(release.stichtag) : '—';
  }

  pillenArt(release: Release): StatusKind {
    return release.status === 'FREIGEGEBEN' ? 'aktiv' : 'pending';
  }

  private oeffnen(release: Release | null): void {
    this.ausgewaehlt.set(release);
    this.dialogOffen.set(true);
  }
}
